import { EVENT_CATEGORIES, EVENT_NAMES, useAnalytics } from '#~/analytics/index.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import { Ikon } from '@studio/design-system/toggle';
import { hasMovement, isNegative, isPositive, Movement } from '@studio/models/ptz';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { twMerge } from 'cn';
import type { GridProps } from './types.js';

/*
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
 * the eight Quick Shot Ikon toggles below carry STagNew tags so the
 * IR-035 Playwright e2e test can locate them via data-stag selectors
 * (the only locator strategy permitted by the platform rule). `Ikon`
 * already spreads `...props` onto the underlying Radix Toggle.Root,
 * so the `data-stag` attribute lands on the rendered <button> with no
 * design-system change.
 */

/** @internal */
const Inner = ({
  capabilities,
  className,
  handler,
  intent,
  isHoming,
  isStopping,
  ...props
}: GridProps): ReactElement => {
  const { track } = useAnalytics();

  return (
    <div className={twMerge('grid grid-cols-4 gap-y-16', className)} {...props}>
      {/* PAN */}
      <Ikon
        disabled={!capabilities[Movement.PanLeft]}
        icon='arrow-left'
        label='Pan Left'
        onPressedChange={(): void => {
          handler.onMovement(Movement.PanLeft, isNegative(intent.pan));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.PanLeft
          });
        }}
        pressed={isNegative(intent.pan)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.pan-left')}
      />

      <Ikon
        disabled={!capabilities[Movement.PanRight]}
        icon='arrow-right'
        label='Pan Right'
        onPressedChange={(): void => {
          handler.onMovement(Movement.PanRight, isPositive(intent.pan));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.PanRight
          });
        }}
        pressed={isPositive(intent.pan)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.pan-right')}
      />

      {/* PAN */}
      <Ikon
        disabled={!capabilities[Movement.TiltUp]}
        icon='arrow-up'
        label='Tilt Up'
        onPressedChange={(): void => {
          handler.onMovement(Movement.TiltUp, isPositive(intent.tilt));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.TiltUp
          });
        }}
        pressed={isPositive(intent.tilt)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.tilt-up')}
      />

      <Ikon
        disabled={!capabilities[Movement.TiltDown]}
        icon='arrow-down'
        label='Tilt Down'
        onPressedChange={(): void => {
          handler.onMovement(Movement.TiltDown, isNegative(intent.tilt));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.TiltDown
          });
        }}
        pressed={isNegative(intent.tilt)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.tilt-down')}
      />

      {/* ZOOM */}
      <Ikon
        disabled={!capabilities[Movement.ZoomIn]}
        icon='zoom-in'
        label='Zoom In'
        onPressedChange={(): void => {
          handler.onMovement(Movement.ZoomIn, isPositive(intent.zoom));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.ZoomIn
          });
        }}
        pressed={isPositive(intent.zoom)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.zoom-in')}
      />

      <Ikon
        disabled={!capabilities[Movement.ZoomOut]}
        icon='zoom-out'
        label='Zoom Out'
        onPressedChange={(): void => {
          handler.onMovement(Movement.ZoomOut, isNegative(intent.zoom));
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: Movement.ZoomOut
          });
        }}
        pressed={isNegative(intent.zoom)}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.zoom-out')}
      />

      {/* HOME */}
      <Ikon
        disabled={!capabilities.home}
        icon='home'
        label='Home'
        onPressedChange={(pressed: boolean): void => {
          if (pressed) {
            handler.onHome();
            track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
              quickShotType: 'home'
            });
          }
        }}
        pressed={isHoming}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.home')}
      />

      {/* STOP */}
      <Ikon
        disabled={!hasMovement(intent)}
        icon='close'
        label='Stop All'
        onPressedChange={(): void => {
          handler.onMovement(false);
          track(EVENT_NAMES.CLICK_QUICK_SHOTS, EVENT_CATEGORIES.CONTROL_TRAY, {
            quickShotType: 'stop-all'
          });
        }}
        pressed={isStopping}
        {...STagNew(Sel.MainRoomRightTrayControls, 'quick-shots.stop-all')}
      />
    </div>
  );
};

/** `<Grid>` component. */
export const Grid = memo(Inner, dequal);
